import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Button,
    Grid,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    PlayArrow,
    EmojiEvents,
    Speed,
    Grid3x3,
    Straighten,
    TipsAndUpdates,
    Lock,
    CloudUpload,
    Loop,
    Code
} from '@mui/icons-material';

import { API_ENDPOINTS } from '../../../utils/config';
import { useAuth } from '../../../Context/AuthContext';
import AgentCodeEditor from './AgentCodeEditor';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { useManualRunRecorder } from '../useManualRunRecorder';
import { normalizeLastRunDoc, shouldShowReplay } from '../useArcadeAgentSession';
import {
    ArcadeReplayDialog,
    LAST_RUN_LOOP_BUTTON_SX,
    LeaderboardWatchButton,
    useArcadeReplay,
} from '../arcadeReplay';
import { leaderboardEntryScore, uploadErrorInfo } from '../arcadeTheme';
import { ArcadeToast, useArcadeToast } from '../useArcadeToast';
import ManualSaveNotice from '../ManualSaveNotice';
import {
    GM_GAME_ID,
    applyGmStateToLocal,
    createSnakeSession,
    deleteSnakeSession,
    moveSnakeSession,
    vecToDirection,
} from './snakeGmClient';
import { gmStartErrorMessage, useGmLease } from '../gmLease';
import GmLeaseNotice from '../GmLeaseNotice';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import { ArcadeShellLayout } from '../ArcadeGameShell';
import LeaderboardPanel from '../ui/LeaderboardPanel';
import GameControls from '../ui/GameControls';
import { useKeyboardStart } from '../manualPlay';
import UiButton from '../ui/Button';

// ---------------------------------------------------------------------------
// Boilerplate template shown in the code editor (snake2d)
// ---------------------------------------------------------------------------
const { header: SNAKE_HEADER, template: GAME_TEMPLATE, footer: SNAKE_FOOTER } = GAME_BOILERPLATE.snake;

const ACCENT = GAME_ACCENT.snake2d;

/** Snake direction for a key, by key name or physical code (arrows and WASD). */
export function snakeKeyDirection(e) {
    switch (e.key) {
        case 'ArrowUp': case 'w': case 'W': return { x: 0, y: -1 };
        case 'ArrowDown': case 's': case 'S': return { x: 0, y: 1 };
        case 'ArrowLeft': case 'a': case 'A': return { x: -1, y: 0 };
        case 'ArrowRight': case 'd': case 'D': return { x: 1, y: 0 };
        default: break;
    }
    switch (e.code) {
        case 'ArrowUp': case 'KeyW': return { x: 0, y: -1 };
        case 'ArrowDown': case 'KeyS': return { x: 0, y: 1 };
        case 'ArrowLeft': case 'KeyA': return { x: -1, y: 0 };
        case 'ArrowRight': case 'KeyD': return { x: 1, y: 0 };
        default: return null;
    }
}

const Snake = () => {
    // Shared Snake GM: started on Start Game, released on game over / leaving.
    const gmLease = useGmLease(GM_GAME_ID);
    const canvasRef = useRef(null);
    const gameLoopRef = useRef(null);
    const sessionIdRef = useRef(null);
    const boardSizeRef = useRef(10);
    const moveInFlightRef = useRef(false);
    const prevScoreRef = useRef(0);

    // Game constants
    const CANVAS_WIDTH = 600;
    const CANVAS_HEIGHT = 600;

    // Theme Colors
    const THEME = {
        bg: COLOR.bg,
        cardBg: COLOR.surface,
        border: COLOR.border,
        primary: COLOR.primary,
        secondary: COLOR.textMuted,
        accent: COLOR.danger,
        grid: COLOR.border
    };

    // Display helpers only — board size comes from the GM (authoritative).
    const getGridDimensions = (boardSize) => {
        const size = Math.max(1, Number(boardSize) || boardSizeRef.current || 10);
        return { width: size, height: size };
    };

    // Game state
    const [gameState, setGameState] = useState('menu'); // 'menu', 'playing', 'paused', 'gameOver'
    const [score, setScore] = useState(0);
    const [speed, setSpeed] = useState(150);
    const [, setDifficultyLevel] = useState(0); // driven by GM board_size when variable_grid
    // Changing grid sizes: default OFF (fixed board). Toggle only from menu.
    const [variableGridEnabled, setVariableGridEnabled] = useState(false);
    const variableGridRef = useRef(false);
    const [showDialog, setShowDialog] = useState(false);
    const [dialogContent, setDialogContent] = useState({ title: '', message: '' });
    const { open: showToast, message: toastMessage, severity: toastSeverity, showToast: showToastMessage, dismiss: dismissToast } = useArcadeToast();
    const [agentFile, setAgentFile] = useState(null);
    const [agentRunLoading, setAgentRunLoading] = useState(false);
    const [agentInputTab, setAgentInputTab] = useState('upload'); // 'upload' | 'editor'
    const [, setLastAgentPlay] = useState(null); // {init_state, move_history, score} - ONLY for uploaded agent
    const [leaderboard, setLeaderboard] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);

    // Game objects
    const snakeRef = useRef([{ x: 10, y: 10 }]);
    const directionRef = useRef({ x: 0, y: 0 });
    const startingRef = useRef(false);
    const pendingDirectionRef = useRef(null);
    const foodRef = useRef({ x: 15, y: 15 });
    const lastMoveTimeRef = useRef(0);
    const [lastExecutedAgentName, setLastExecutedAgentName] = useState('');
    const [lastExecutedTime, setLastExecutedTime] = useState(null);
    const [lastExecutedScore, setLastExecutedScore] = useState(null);
    const [lastRunEntry, setLastRunEntry] = useState(null);
    const [lastRunLoading, setLastRunLoading] = useState(true);
    const lastRunRequestIdRef = useRef(0);
    const replay = useArcadeReplay();
    const { token, user } = useAuth();
    const foodHistoryRef = useRef([]);
    const refreshSnakeBoard = useCallback(async () => {
        try {
            const res = await fetch(API_ENDPOINTS.SNAKE_LEADERBOARD);
            const data = await res.json();
            setLeaderboard(data.leaderboard || []);
        } catch {
            /* keep existing board */
        }
    }, []);
    const snakeSession = useMemo(() => ({ refreshLeaderboard: refreshSnakeBoard }), [refreshSnakeBoard]);
    const recorder = useManualRunRecorder({
        gameId: 'snake2d',
        copyToMoveHistory: true,
        session: snakeSession,
    });
    const bestLoggedScore = useMemo(() => {
        if (!leaderboard.length) return 0;
        const best = Number(leaderboardEntryScore(leaderboard[0]));
        return Number.isFinite(best) ? best : 0;
    }, [leaderboard]);

    const formatAuthTimestamp = useCallback((ts) => {
        if (!ts) return '-';
        let date;
        // If string (ISO) and missing Z/offset, assume UTC (server standard)
        if (typeof ts === 'string' && ts.includes('T') && !ts.endsWith('Z') && !ts.includes('+')) {
            date = new Date(ts + 'Z');
        } else {
            date = new Date(ts);
        }
        return date.toLocaleString('en-US', {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        });
    }, []);

    const userId = user?._id || user?.id || null;
    const userRole = user?.user_type || user?.role || (token ? 'registered' : 'guest');

    const hydrateLastRun = useCallback((doc) => {
        const normalized = normalizeLastRunDoc(doc);
        if (!normalized) {
            setLastExecutedAgentName('');
            setLastExecutedTime(null);
            setLastExecutedScore(null);
            setLastAgentPlay(null);
            setLastRunEntry(null);
            return;
        }
        setLastExecutedAgentName(normalized.name || '');
        setLastExecutedTime(normalized.time ? new Date(normalized.time).getTime() : Date.now());
        setLastExecutedScore(normalized.score);
        setLastRunEntry(normalized.raw || null);
        const raw = normalized.raw || {};
        const moveHistory = Array.isArray(raw.move_history)
            ? raw.move_history
            : Array.isArray(raw.history)
                ? raw.history
                : [];
        if (raw.init_state && moveHistory.length > 0) {
            setLastAgentPlay({
                init_state: raw.init_state,
                move_history: moveHistory,
                history: Array.isArray(raw.history) ? raw.history : [],
                food_history: Array.isArray(raw.food_history) ? raw.food_history : [],
                score: normalized.score,
            });
        } else {
            setLastAgentPlay((prev) => (prev ? prev : null));
        }
    }, []);

    const fetchLastRun = useCallback(async () => {
        const requestId = lastRunRequestIdRef.current + 1;
        lastRunRequestIdRef.current = requestId;
        setLastRunLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.LAST_RUN('snake2d', { userId, role: userRole }));
            if (!res.ok) throw new Error(`last run fetch failed (${res.status})`);
            const data = await res.json();
            if (lastRunRequestIdRef.current !== requestId) return;
            hydrateLastRun(data?.run);
        } catch (err) {
            if (lastRunRequestIdRef.current !== requestId) return;
            // Keep prior card state on transient fetch failures.
            console.warn('Snake last-run fetch failed', err);
        } finally {
            if (lastRunRequestIdRef.current === requestId) {
                setLastRunLoading(false);
            }
        }
    }, [hydrateLastRun, userId, userRole]);

    const endEpisode = useCallback((finalScore, allowVariable) => {
        setGameState('gameOver');
        setShowDialog(true);
        setDialogContent({
            title: 'Game Over',
            message: `Final Score: ${finalScore}\n${finalScore > bestLoggedScore ? 'New High Score!' : `High Score: ${bestLoggedScore}`}`
        });
        recorder.finalizeAndUpload({
            score: finalScore,
            extra: {
                food_history: foodHistoryRef.current,
                board_size: boardSizeRef.current,
                variable_grid: allowVariable,
            },
        });
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteSnakeSession(sid);
    }, [bestLoggedScore, recorder]);

    // One GM-authoritative tick (thin FE — no local collision / food / grow logic).
    const moveSnake = useCallback(async () => {
        if (moveInFlightRef.current) return;
        const direction = vecToDirection(directionRef.current);
        if (!direction) return;
        const sid = sessionIdRef.current;
        if (!sid) return;

        moveInFlightRef.current = true;
        try {
            const allowVariable = variableGridRef.current;
            const data = await moveSnakeSession(sid, direction);
            const prevBoard = boardSizeRef.current;
            const meta = applyGmStateToLocal(data, {
                snakeRef,
                foodRef,
                setScore,
                setDifficultyLevel,
            });
            if (Array.isArray(data.food_history)) {
                foodHistoryRef.current = data.food_history;
            }

            // Move responses carry only the newest history entry (last_event).
            const hist = Array.isArray(data.history) ? data.history : [];
            const last = data.last_event || (hist.length ? hist[hist.length - 1] : null);
            if (last) {
                recorder.recordTick({
                    action: last.action || direction,
                    snake: last.snake,
                    food: last.food,
                    score: last.score,
                    board_size: last.board_size || meta.boardSize,
                    variable_grid: last.variable_grid ?? allowVariable,
                });
            }

            if (typeof data.score === 'number' && data.score > prevScoreRef.current) {
                showToastMessage('Yummy! +10 points', 'success');
                if (allowVariable && meta.boardSize > prevBoard) {
                    setTimeout(() => {
                        showToastMessage(`Level up! Grid: ${meta.boardSize}x${meta.boardSize}`, 'info');
                    }, 400);
                }
                setSpeed((prev) => Math.max(100, prev - 1));
            }
            prevScoreRef.current = typeof data.score === 'number' ? data.score : prevScoreRef.current;
            boardSizeRef.current = meta.boardSize;

            if (meta.gameOver) {
                endEpisode(meta.score, allowVariable);
            }
        } catch (err) {
            console.warn('Snake GM move failed', err);
            showToastMessage('Lost connection to game server', 'error');
            setGameState('paused');
        } finally {
            moveInFlightRef.current = false;
        }
    }, [endEpisode, recorder, showToastMessage]);

    // Change direction (GM also rejects 180° reverses)
    const changeDirection = useCallback((newDirection) => {
        const currentDir = directionRef.current;
        if (newDirection.x === -currentDir.x && newDirection.y === -currentDir.y) {
            return;
        }
        directionRef.current = newDirection;
    }, []);

    // Initialize via GM session create (authoritative init_state)
    const initializeGame = useCallback(async () => {
        const allowVariable = variableGridEnabled;
        variableGridRef.current = allowVariable;
        if (sessionIdRef.current) {
            await deleteSnakeSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }

        const data = await createSnakeSession({ variableGrid: allowVariable });
        if (!data || !data.session_id) {
            throw new Error('Snake GM did not return a session_id');
        }
        sessionIdRef.current = data.session_id;
        directionRef.current = { x: 0, y: 0 };
        prevScoreRef.current = 0;
        setSpeed(150);
        lastMoveTimeRef.current = 0;

        const meta = applyGmStateToLocal(data, {
            snakeRef,
            foodRef,
            setScore,
            setDifficultyLevel,
        });
        boardSizeRef.current = meta.boardSize;
        foodHistoryRef.current = Array.isArray(data.food_history) ? data.food_history : [];

        const init = data.init_state || {};
        recorder.reset({
            init_state: {
                snake: Array.isArray(init.snake)
                    ? init.snake.map((seg) => (Array.isArray(seg) ? { x: seg[1], y: seg[0] } : seg))
                    : snakeRef.current.map((seg) => ({ ...seg })),
                food: init.food
                    ? (Array.isArray(init.food) ? { x: init.food[1], y: init.food[0] } : init.food)
                    : { ...foodRef.current },
                board_size: meta.boardSize,
                variable_grid: allowVariable,
            },
            food_history: foodHistoryRef.current,
            variable_grid: allowVariable,
        });
    }, [recorder, variableGridEnabled]);

    // --- Leaderboard and Agent integration ---
    // Fetch leaderboard
    useEffect(() => {
        let mounted = true;
        async function fetchLeaderboard() {
            setLeaderboardLoading(true);
            try {
                const res = await fetch(API_ENDPOINTS.SNAKE_LEADERBOARD);
                const data = await res.json();
                if (mounted) setLeaderboard(data.leaderboard || []);
            } catch (e) {
                if (mounted) setLeaderboard([]);
            }
            if (mounted) setLeaderboardLoading(false);
        }
        fetchLeaderboard();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        fetchLastRun();
    }, [fetchLastRun]);

    const handleAgentFileChange = (e) => {
        setAgentFile(e.target.files[0]);
    };
    // try to play a leaderboard entry's replay (or stop if same entry playing)
    const handlePlayLeaderboardEntry = (entry) => {
        replay.openReplay(entry, { loop: false });
    };

    const handleRunAgent = async () => {
        // In AuthContext, guests have token = null or user_type = 'guest'
        if (!token) {
            showToastMessage('Sign in or continue as guest to upload agents.', 'error');
            return;
        }

        if (!agentFile) return showToastMessage('No agent selected', 'warning');

        setAgentRunLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', agentFile);

            const res = await fetch(API_ENDPOINTS.SNAKE_UPLOAD_AGENT, {
                method: 'POST',
                headers: {
                    // This header is required to bypass the 403 Check
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const json = await res.json().catch(() => ({}));

            if (!res.ok) {
                // Prefer the server's reason (guest 403, gateway 401); bare 401 = expired login.
                showToastMessage(uploadErrorInfo(res.status, json, 'Agent run failed').message, 'error');
            } else {
                console.log("Upload Response (full):", json);

                // Check for nested data structures - API might return data in different formats
                const resultData = json.result || json.data || json.entry || json;

                const playData = {
                    init_state: resultData.init_state || json.init_state,
                    food_history: resultData.food_history || json.food_history || [],
                    move_history: resultData.move_history || json.move_history || [],
                    history: resultData.history || json.history || [],
                    score: resultData.score || json.score || 0
                };

                console.log("Extracted play data:", playData);
                console.log("move_history length:", playData.move_history?.length);

                setLastAgentPlay(playData);

                // Refresh leaderboard FIRST to get the authoritative agent_id
                let authoritativeName = null;
                try {
                    const r2 = await fetch(API_ENDPOINTS.SNAKE_LEADERBOARD);
                    const d2 = await r2.json();
                    const newLeaderboard = d2.leaderboard || [];
                    setLeaderboard(newLeaderboard);

                    // Attempt to find the entry we just added
                    const uploadedId = json._id || json.id || json.db_ref;
                    if (uploadedId) {
                        const match = newLeaderboard.find(e => e._id === uploadedId || e.id === uploadedId || e.run_id === uploadedId);
                        if (match && match.agent_id) {
                            authoritativeName = match.agent_id;
                        }
                    }
                } catch (e) {
                    console.error("Failed to refresh leaderboard", e);
                }

                // Initial name fallback
                let nameToSet = authoritativeName ||
                    (json.entry && (json.entry.agent_id || json.entry.agent)) ||
                    (json.agent_id || json.agent) ||
                    json.id ||
                    json._id;

                // Only truncate/format if we don't have a good name
                if (nameToSet && !nameToSet.toString().startsWith('agent_') && /^[a-f0-9]{24}$/.test(nameToSet)) {
                    nameToSet = `agent_${nameToSet.substring(0, 8)}`;
                }

                setLastExecutedAgentName(nameToSet || 'Unknown Agent');

                const infoTime = (json.timestamp || (json.entry && json.entry.timestamp) || (json.data && json.data.timestamp));
                setLastExecutedTime(infoTime ? new Date(infoTime).getTime() : Date.now());
                setLastExecutedScore(playData?.score ?? 0);
                await fetchLastRun();
            }
        } catch (e) {
            console.error(e);
            showToastMessage('Error running agent', 'error');
        }
        setAgentRunLoading(false);
    };

    const handleViewLastPlay = () => {
        if (!lastRunEntry) return showToastMessage('No agent play available', 'warning');
        replay.openReplay(lastRunEntry, { loop: false });
    };

    const handleLoopLastPlay = () => {
        if (!lastRunEntry) return showToastMessage('No agent play available', 'warning');
        replay.openReplay(lastRunEntry, { loop: true });
    };

    // Start game
    const startGame = async () => {
        startingRef.current = true;
        pendingDirectionRef.current = null;
        try {
            await initializeGame();
            if (pendingDirectionRef.current) {
                directionRef.current = pendingDirectionRef.current;
                pendingDirectionRef.current = null;
            }
            startingRef.current = false;
            setGameState('playing');
            showToastMessage('Game Started!', 'info');
        } catch (err) {
            startingRef.current = false;
            console.error(err);
            showToastMessage(gmStartErrorMessage(err), 'error');
        }
    };

    const returnToMenu = useCallback(() => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteSnakeSession(sid);
        dismissToast();
        setGameState('menu');
    }, [dismissToast]);

    // Pause/Resume game
    const togglePause = useCallback(() => {
        if (gameState === 'playing') {
            setGameState('paused');
            showToastMessage('Game Paused', 'warning');
        } else {
            setGameState('playing');
            showToastMessage('Game Resumed', 'info');
        }
    }, [gameState, showToastMessage]);

    // Enter / Space start from the menu or game-over screen.
    useKeyboardStart({
        active: gameState === 'menu' || gameState === 'gameOver',
        onStart: startGame,
    });

    // Keyboard controls
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Handle pause/resume in any playing state
            if ((gameState === 'playing' || gameState === 'paused') && e.key === 'Escape') {
                togglePause();
                e.preventDefault();
                return;
            }

            // Resume with space when paused
            if (gameState === 'paused' && e.key === ' ') {
                togglePause();
                e.preventDefault();
                return;
            }

            const dir = snakeKeyDirection(e);
            if (dir) e.preventDefault();
            // A key pressed while the game is still starting (GM cold start)
            // becomes the first move instead of being dropped.
            if (dir && startingRef.current) {
                pendingDirectionRef.current = dir;
                return;
            }

            // Only handle movement controls when actually playing
            if (gameState !== 'playing') return;
            if (dir) changeDirection(dir);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [gameState, changeDirection, togglePause]);

    // Game loop — render locally; advance via GM on tick interval
    const gameLoop = useCallback((timestamp) => {
        const { width: gridWidth, height: gridHeight } = getGridDimensions(boardSizeRef.current);
        const cellSize = Math.floor(CANVAS_WIDTH / gridWidth);

        // Drawing after unmount (or before the canvas mounts) is a no-op.
        const canvas = canvasRef.current;
        if (!canvas || !canvas.isConnected) return;

        const ctx = canvas.getContext ? canvas.getContext('2d') : null;
        if (!ctx) return;

        // Clear canvas with Theme Background
        ctx.fillStyle = '#0a0a0a'; // Dark theme bg
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        // Draw grid lines (subtle)
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 1;
        for (let x = 0; x <= gridWidth; x++) {
            ctx.beginPath();
            ctx.moveTo(x * cellSize, 0);
            ctx.lineTo(x * cellSize, CANVAS_HEIGHT);
            ctx.stroke();
        }
        for (let y = 0; y <= gridHeight; y++) {
            ctx.beginPath();
            ctx.moveTo(0, y * cellSize);
            ctx.lineTo(CANVAS_WIDTH, y * cellSize);
            ctx.stroke();
        }

        // Draw snake
        snakeRef.current.forEach((segment, index) => {
            if (index === 0) {
                // Head - Brand Green
                ctx.fillStyle = '#2cbb5d';
            } else {
                // Body - Darker Green
                ctx.fillStyle = '#1e8e45';
            }

            // Rounded Rectangles for segments
            const gap = 1;
            ctx.fillRect(
                segment.x * cellSize + gap,
                segment.y * cellSize + gap,
                cellSize - gap * 2,
                cellSize - gap * 2
            );

            // Eyes for the head
            if (index === 0) {
                ctx.fillStyle = '#0a0a0a';
                const eyeSize = Math.max(2, Math.floor(cellSize / 6));

                // Simplified eye logic for cleaner look
                const centerX = segment.x * cellSize + cellSize / 2;
                const centerY = segment.y * cellSize + cellSize / 2;
                const offset = cellSize / 4;

                // Draw eyes roughly based on direction or default
                ctx.beginPath();
                ctx.arc(centerX - offset / 2, centerY - offset / 2, eyeSize, 0, Math.PI * 2);
                ctx.arc(centerX + offset / 2, centerY - offset / 2, eyeSize, 0, Math.PI * 2);
                ctx.fill();
            }
        });

        // Draw food - Brand Red
        ctx.fillStyle = '#ef4743';
        const foodPadding = cellSize * 0.15;
        ctx.beginPath();
        ctx.roundRect(
            foodRef.current.x * cellSize + foodPadding,
            foodRef.current.y * cellSize + foodPadding,
            cellSize - foodPadding * 2,
            cellSize - foodPadding * 2,
            4
        );
        ctx.fill();

        // Draw pause overlay
        if (gameState === 'paused') {
            ctx.fillStyle = 'rgba(10, 10, 10, 0.8)';
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            ctx.fillStyle = '#2cbb5d';
            ctx.font = 'bold 48px "Fira Code", monospace';
            ctx.textAlign = 'center';
            ctx.fillText('PAUSED', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);

            ctx.font = '16px "Roboto", sans-serif';
            ctx.fillStyle = '#a1a1aa';
            ctx.fillText('Press Esc or Space to continue', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 40);
        }

        // Game tick
        if (gameState !== 'playing') return;

        if (timestamp - lastMoveTimeRef.current >= speed) {
            moveSnake();
            lastMoveTimeRef.current = timestamp;
        }
    }, [gameState, speed, moveSnake]);

    // Start game loop
    useEffect(() => {
        if (gameState === 'playing' || gameState === 'paused') {
            let active = true;
            const loop = (timestamp) => {
                if (!active) return;
                gameLoop(timestamp);
                if (active) gameLoopRef.current = requestAnimationFrame(loop);
            };
            gameLoopRef.current = requestAnimationFrame(loop);
            return () => {
                active = false;
                if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
                gameLoopRef.current = null;
            };
        }
        if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
        gameLoopRef.current = null;
        return undefined;
    }, [gameState, gameLoop]);

    // Cleanup GM session on unmount
    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteSnakeSession(sid);
    }, []);

    return (
        <ArcadeShellLayout
            title="Snake"
            titleAccent="Evolution"
            subtitle="Train your AI agent or play manually to top the leaderboard"
            accentColor={ACCENT}
            leaderboard={(
                            <Stack spacing={3}>
                                <LeaderboardPanel
                                    entries={leaderboard}
                                    loading={leaderboardLoading}
                                    accentColor={ACCENT}
                                    formatScore={(entry) => String(leaderboardEntryScore(entry))}
                                    emptyHint="No scores yet."
                                    limit={10}
                                    renderAction={(entry) => (
                                        <LeaderboardWatchButton
                                            entry={entry}
                                            onWatch={handlePlayLeaderboardEntry}
                                            accentColor={THEME.secondary}
                                        />
                                    )}
                                />

                                <Paper sx={{
                                    p: 2,
                                    bgcolor: THEME.cardBg,
                                    border: `1px solid ${THEME.border}`,
                                    borderRadius: 3
                                }}>
                                    <Typography variant="subtitle2" sx={{ color: THEME.secondary, mb: 2, letterSpacing: 1 }}>Live stats</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmojiEvents fontSize="inherit" /> Score
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: THEME.primary }}>{score}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Straighten fontSize="inherit" /> Length
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: 'white' }}>{snakeRef.current.length}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Speed fontSize="inherit" /> Speed
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: THEME.primary }}>{Math.round((200 - speed) / 2)}%</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Grid3x3 fontSize="inherit" /> Grid
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: '#fff' }}>
                                                    {(() => {
                                                        const size = gameState === 'menu' ? 10 : boardSizeRef.current;
                                                        return `${size}x${size}`;
                                                    })()}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmojiEvents fontSize="inherit" /> Best Score
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: '#fbbf24' }} data-testid="snake-best-score">{bestLoggedScore}</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Stack>
                        )}
            play={(
                            <Box sx={{ position: 'relative' }}>
                                <Paper
                                    elevation={24}
                                    sx={{
                                        p: 1,
                                        bgcolor: '#000',
                                        border: `1px solid ${THEME.border}`,
                                        borderRadius: 4,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}
                                >
                                    <canvas
                                        ref={canvasRef}
                                        width={CANVAS_WIDTH}
                                        height={CANVAS_HEIGHT}
                                        style={{
                                            borderRadius: '12px',
                                            maxWidth: '100%',
                                            height: 'auto',
                                            display: 'block',
                                            boxShadow: '0 0 40px rgba(44, 187, 93, 0.1)'
                                        }}
                                    />

                                    {/* Overlay for Menu */}
                                    {gameState === 'menu' && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0, left: 0, right: 0, bottom: 0,
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                            backdropFilter: 'blur(4px)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10
                                        }}>
                                            <UiButton size="lg" onClick={startGame} startIcon={<PlayArrow />}>
                                                Start Game
                                            </UiButton>
                                            <GmLeaseNotice lease={gmLease} color={THEME.primary} />
                                            <FormControlLabel
                                                sx={{ mt: 2, color: THEME.secondary }}
                                                control={
                                                    <Switch
                                                        checked={variableGridEnabled}
                                                        onChange={(e) => setVariableGridEnabled(e.target.checked)}
                                                        size="small"
                                                        data-testid="snake-variable-grid-toggle"
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': { color: THEME.primary },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                bgcolor: THEME.primary,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ color: THEME.secondary }}>
                                                        Changing grid sizes
                                                    </Typography>
                                                }
                                            />
                                            <Typography variant="caption" sx={{ color: COLOR.textSubtle, mt: 0.5, maxWidth: 260, textAlign: 'center' }}>
                                                {variableGridEnabled
                                                    ? 'Grid expands as your score rises (up to 30×30)'
                                                    : 'Fixed 10×10 grid (default)'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>

                                {/* Quick Controls Bar */}
                                <GameControls
                                    phase={gameState === 'gameOver' ? 'over' : gameState}
                                    onStart={startGame}
                                    onPause={togglePause}
                                    onEnd={returnToMenu}
                                />
                            </Box>
                        )}
            agent={(
                            <Stack spacing={3}>
                                <Paper sx={{ p: 3, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <Code sx={{ color: THEME.primary }} />
                                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>Agent Mode</Typography>
                                    </Box>

                                    {(!token) ? (
                                        <Box sx={{
                                            border: `1px dashed ${COLOR.borderStrong}`,
                                            borderRadius: 2,
                                            p: 3,
                                            textAlign: 'center',
                                            mb: 2,
                                            bgcolor: 'rgba(255,255,255,0.02)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            <Lock sx={{ color: THEME.secondary, fontSize: 30 }} />
                                            <Typography variant="body2" sx={{ color: THEME.secondary }}>
                                                Sign in to upload an agent.
                                            </Typography>
                                            <Link to="/auth/login" style={{ textDecoration: 'none' }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ mt: 1, borderColor: THEME.primary, color: THEME.primary }}
                                                >
                                                    Sign in
                                                </Button>
                                            </Link>
                                        </Box>
                                    ) : (
                                        // --- REGISTERED VIEW ---
                                        <>
                                            {/* Tab switcher: Upload vs Editor */}
                                            <Box sx={{ display: 'flex', mb: 2, borderBottom: `1px solid ${THEME.border}` }}>
                                                {['upload', 'editor'].map(tab => (
                                                    <Button
                                                        key={tab}
                                                        size="small"
                                                        onClick={() => setAgentInputTab(tab)}
                                                        sx={{
                                                            flex: 1,
                                                            textTransform: 'capitalize',
                                                            borderRadius: 0,
                                                            borderBottom: agentInputTab === tab ? `2px solid ${THEME.primary}` : '2px solid transparent',
                                                            color: agentInputTab === tab ? THEME.primary : THEME.secondary,
                                                            fontWeight: agentInputTab === tab ? 700 : 400,
                                                            pb: 1,
                                                        }}
                                                    >
                                                        {tab === 'upload' ? 'Upload File' : 'Code Editor'}
                                                    </Button>
                                                ))}
                                            </Box>

                                            {agentInputTab === 'upload' ? (
                                                /* Upload file panel */
                                                <>
                                                    <Box sx={{
                                                        border: `1px dashed ${COLOR.borderStrong}`,
                                                        borderRadius: 2,
                                                        p: 2,
                                                        textAlign: 'center',
                                                        mb: 2,
                                                        bgcolor: 'rgba(255,255,255,0.02)'
                                                    }}>
                                                        <input
                                                            id="agent-upload"
                                                            type="file"
                                                            accept=".py"
                                                            style={{ display: 'none' }}
                                                            onChange={handleAgentFileChange}
                                                        />
                                                        <label htmlFor="agent-upload">
                                                            <Button
                                                                component="span"
                                                                startIcon={<CloudUpload />}
                                                                sx={{ color: THEME.secondary, textTransform: 'none' }}
                                                            >
                                                                {agentFile ? agentFile.name : 'Select Python Agent'}
                                                            </Button>
                                                        </label>
                                                    </Box>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={handleRunAgent}
                                                        disabled={!agentFile || agentRunLoading}
                                                        sx={{
                                                            bgcolor: '#2563eb',
                                                            color: 'white',
                                                            mb: 2,
                                                            '&:disabled': { bgcolor: '#1e293b', color: '#64748b' }
                                                        }}
                                                    >
                                                        {agentRunLoading ? 'Uploading Agent...' : 'Upload Agent'}
                                                    </Button>
                                                </>
                                            ) : (
                                                /* Code editor panel */
                                                <AgentCodeEditor
                                                    header={SNAKE_HEADER}
                                                    template={GAME_TEMPLATE}
                                                    footer={SNAKE_FOOTER}
                                                    loading={agentRunLoading}
                                                    theme={THEME}
                                                    onSubmit={(code) => {
                                                        const blob = new Blob([code], { type: 'text/x-python' });
                                                        const file = new File([blob], 'agent.py', { type: 'text/x-python' });
                                                        setAgentFile(file);
                                                        // Trigger run immediately via a short timeout so state settles
                                                        setTimeout(() => {
                                                            const formData = new FormData();
                                                            formData.append('file', file);
                                                            setAgentRunLoading(true);
                                                            fetch(API_ENDPOINTS.SNAKE_UPLOAD_AGENT, {
                                                                method: 'POST',
                                                                headers: { 'Authorization': `Bearer ${token}` },
                                                                body: formData
                                                            })
                                                            .then(r => r.json())
                                                            .then(json => {
                                                                if (!json || json.error) {
                                                                    showToastMessage(json?.error || 'Agent run failed', 'error');
                                                                    return;
                                                                }
                                                                const resultData = json.result || json.data || json.entry || json;
                                                                setLastAgentPlay({
                                                                    init_state: resultData.init_state || json.init_state,
                                                                    food_history: resultData.food_history || json.food_history || [],
                                                                    move_history: resultData.move_history || json.move_history || [],
                                                                    history: resultData.history || json.history || [],
                                                                    score: resultData.score || json.score || 0
                                                                });
                                                                setLastExecutedAgentName(json.agent_id || (json.entry && json.entry.agent_id) || 'agent_editor');
                                                                setLastExecutedTime(Date.now());
                                                                setLastExecutedScore((resultData.score || json.score || 0));
                                                                showToastMessage('Agent simulation complete!', 'success');
                                                                fetchLastRun().catch(() => {});
                                                                fetch(API_ENDPOINTS.SNAKE_LEADERBOARD)
                                                                    .then(r => r.json())
                                                                    .then(d => setLeaderboard(d.leaderboard || d || []))
                                                                    .catch(() => {});
                                                            })
                                                            .catch(e => { console.error(e); showToastMessage('Error running agent', 'error'); })
                                                            .finally(() => setAgentRunLoading(false));
                                                        }, 0);
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ color: THEME.secondary, display: 'block', mb: 0.5, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                                            Last run
                                        </Typography>
                                        {lastExecutedAgentName ? (
                                            <Paper sx={{
                                                p: 1.5,
                                                bgcolor: 'rgba(44, 187, 93, 0.1)',
                                                border: `1px solid ${THEME.primary}`,
                                                borderRadius: 2
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                                                    {lastExecutedAgentName.startsWith('agent_') ? lastExecutedAgentName : `agent_${lastExecutedAgentName}`}
                                                </Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        label={`Score: ${lastExecutedScore ?? 0}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: THEME.primary,
                                                            color: 'black',
                                                            fontWeight: 'bold',
                                                            height: 20,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                    <Typography variant="caption" sx={{ color: COLOR.textMuted }}>
                                                        {formatAuthTimestamp(lastExecutedTime)}
                                                    </Typography>
                                                    {shouldShowReplay(lastRunEntry, false, { only_replayable: true }) ? (
                                                        <>
                                                            <LeaderboardWatchButton
                                                                entry={lastRunEntry}
                                                                onWatch={handleViewLastPlay}
                                                                accentColor={THEME.primary}
                                                                playLabel="Play replay"
                                                                tooltipLabel="Play replay"
                                                            />
                                                            <Button
                                                                size="small"
                                                                data-testid="last-run-loop"
                                                                onClick={handleLoopLastPlay}
                                                                aria-label="Loop run"
                                                                title="Loop run"
                                                                sx={LAST_RUN_LOOP_BUTTON_SX(THEME.primary)}
                                                            >
                                                                <Loop fontSize="small" />
                                                            </Button>
                                                        </>
                                                    ) : null}
                                                </Stack>
                                            </Paper>
                                        ) : (
                                            <Paper sx={{
                                                p: 1.5,
                                                bgcolor: 'rgba(255,255,255,0.04)',
                                                border: `1px solid ${THEME.border}`,
                                                borderRadius: 2
                                            }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary }}>
                                                    {lastRunLoading ? 'Loading...' : 'No runs yet'}
                                                </Typography>
                                            </Paper>
                                        )}
                                    </Box>
                                </Paper>

                                {/* Controls Guide (Unchanged) */}
                                <Paper sx={{
                                    p: 3,
                                    bgcolor: THEME.cardBg,
                                    border: `1px solid ${THEME.border}`,
                                    borderRadius: 3
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                        <TipsAndUpdates sx={{ color: '#fbbf24' }} />
                                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>Controls</Typography>
                                    </Box>

                                    <Stack spacing={2}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography sx={{ color: THEME.secondary }}>Move</Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {['W', 'A', 'S', 'D'].map(k => (
                                                    <Box key={k} sx={{
                                                        width: 24, height: 24,
                                                        bgcolor: COLOR.border,
                                                        borderRadius: 1,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: 'white', fontSize: '0.75rem', fontWeight: 'bold'
                                                    }}>{k}</Box>
                                                ))}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography sx={{ color: THEME.secondary }}>Pause</Typography>
                                            <Box sx={{
                                                px: 1, height: 24,
                                                bgcolor: COLOR.border,
                                                borderRadius: 1,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: 'white', fontSize: '0.75rem', fontWeight: 'bold'
                                            }}>SPACE</Box>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Stack>
                        )}
        >
            <Container maxWidth="xl">
                <ArcadeToast open={showToast} message={toastMessage} severity={toastSeverity} />

                    {/* Game Over Dialog */}
                    <Dialog
                        open={showDialog}
                        onClose={() => setShowDialog(false)}
                        PaperProps={{
                            sx: {
                                bgcolor: COLOR.surface,
                                border: `1px solid ${THEME.border}`,
                                borderRadius: 3,
                                minWidth: 300
                            }
                        }}
                    >
                        <DialogTitle sx={{ textAlign: 'center', color: 'white', pt: 3 }}>
                            {dialogContent.title}
                        </DialogTitle>
                        <DialogContent sx={{ textAlign: 'center', color: THEME.secondary }}>
                            <Typography variant="h4" sx={{ color: THEME.primary, my: 2, fontWeight: 'bold' }}>
                                {dialogContent.message.split('\n')[0].replace('Final Score: ', '')}
                            </Typography>
                            <Typography variant="body2">Points Scored</Typography>
                            <ManualSaveNotice notice={recorder.saveNotice} color={THEME.primary} />
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', pb: 4, gap: 2 }}>
                            <Button
                                onClick={() => { setShowDialog(false); returnToMenu(); }}
                                variant="outlined"
                                sx={{ color: 'white', borderColor: COLOR.border }}
                            >
                                Menu
                            </Button>
                            <Button
                                onClick={() => { setShowDialog(false); startGame(); }}
                                variant="contained"
                                autoFocus
                                sx={{ bgcolor: THEME.primary, color: 'black', fontWeight: 'bold', '&:hover': { bgcolor: '#249a4d' } }}
                            >
                                Play Again
                            </Button>
                        </DialogActions>
                    </Dialog>
            </Container>
            <ArcadeReplayDialog
                open={replay.open}
                loading={replay.loading}
                error={replay.error}
                run={replay.run}
                startLoop={replay.startLoop}
                onClose={replay.closeReplay}
            />
        </ArcadeShellLayout>
    );
};

export default Snake;